import { useSyncExternalStore } from 'react';

/**
 * While Paige releases, the engine host restarts (about 25 s) and the API answers its channel with
 * a 503 `EngineUnavailable` (the host refused the connection, so nothing was changed). This is the
 * one shared "the engine is updating" state every engine caller goes through (engine-call.ts):
 *
 * - The first caller to see the 503 starts **one** probe, the engine's `health` command, asked
 *   after 2 s and then every 5 s. A 25 s restart costs about five failed probe lines in the
 *   browser's console, not one red line per caller per retry.
 * - Every engine call made while the probe runs waits without a request; identical calls (the same
 *   channel, command and arguments: a poller's next tick) share one place in the queue.
 * - When the probe is answered, the queued calls go, once each.
 *
 * The workbench shows one calm line while anything waits (EngineUpdatingNotice).
 */
let waiting = 0;
let probing: Promise<void> | null = null;
const queued = new Map<string, Promise<unknown>>();
const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of listeners) listener();
}

/** Runs `wait` counted as a call waiting for the engine to come back. */
async function counted<T>(wait: () => Promise<T>): Promise<T> {
  waiting += 1;
  if (waiting === 1) changed();
  try {
    return await wait();
  } finally {
    waiting -= 1;
    if (waiting === 0) changed();
  }
}

/** Whether the engine is known to be restarting: new calls wait for the probe instead of asking. */
export function engineDown(): boolean {
  return probing !== null;
}

/** Whether a call is waiting for the engine now (the workbench says Paige is updating). */
export function engineUpdating(): boolean {
  return waiting > 0 || probing !== null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useEngineUpdating(): boolean {
  return useSyncExternalStore(subscribe, engineUpdating, engineUpdating);
}

/** How long the probe waits before asking: 2 s, then every 5 s. */
export function probeDelay(attempt: number): number {
  return attempt === 0 ? 2_000 : 5_000;
}

/**
 * Resolves when the engine answers again. The first caller's `probe` is the one asked; a caller
 * arriving while it runs joins it. A probe that throws (a proxy's error page) counts as not yet.
 */
export function engineBack(probe: () => Promise<boolean>): Promise<void> {
  if (probing) return probing;
  probing = (async () => {
    for (let attempt = 0; ; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, probeDelay(attempt)));
      if (await probe().catch(() => false)) return;
    }
  })().finally(() => {
    probing = null;
    changed();
  });
  changed();
  return probing;
}

/**
 * Waits for the engine, then runs `ask`; `ask` answers `undefined` when the engine was restarting
 * again, and the wait starts over. Identical calls queued meanwhile (`key`) share the one answer.
 */
export function queueForEngine<T>(
  key: string,
  probe: () => Promise<boolean>,
  ask: () => Promise<{ restarting: true } | { restarting: false; answer: () => T }>,
): Promise<T> {
  const already = queued.get(key);
  if (already) return already as Promise<T>;
  const call = counted(async () => {
    for (;;) {
      await engineBack(probe);
      const reply = await ask();
      if (!reply.restarting) return reply.answer();
    }
  }).finally(() => queued.delete(key));
  queued.set(key, call);
  return call;
}

/** The API's answer while the engine host restarts (EngineUnreachableHandler): safe to ask again. */
export function isEngineRestarting(status: number, body: unknown): boolean {
  return (
    status === 503 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { title?: unknown }).title === 'EngineUnavailable'
  );
}
